<script>
import {defineComponent} from "vue";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import DonationProgressBar from "../DonationProgressBar.vue";

export default defineComponent({
  components: {DonationProgressBar},
  props: {
    /**
     * The total sum of the donations to the campaign.
     */
    currDonationsTotal: {
      type: Number,
      default: 0
    },
    /**
     * The campaign donation goal.
     */
    donationGoal: {
      type: Number,
      default: 0
    },
    /**
     * Text color of card.
     */
    textColor: {
      type: String,
      default: '#000000'
    },
    /**
     * The base color of the unfilled donation bar.
     */
    donationBarBaseColor: {
      type: String,
      default: "#F3F2F2" // $color-section-bg
    },
    /**
     * The color of the filled portion of the donation bar.
     */
    donationBarFillColor: {
      type: String,
      default: "#085E2DFF"
    },
    /**
     * Will increase the font size of the donation total that appears when no goal is set.
     * When false, size will be body-1.
     * When true, size will be subtitle.
     */
    largeTotal: {
      type: Boolean,
      default: false
    },
    endDate: {
      type: String,
      required: false
    },
    showBar: {
      type: Boolean,
      default: true
    },
    showAmount: {
      type: Boolean,
      default: true
    }
  },
  setup() {
  },
  data() {
    return {};
  },
  computed: {
    donationBarFillWidth() {
      if (this.currDonationsTotal >= this.donationGoal) {
        return '100%';
      } else if (this.currDonationsTotal === 0 || this.donationGoal === 0) {
        return '0%';
      } else {
        let percent = Math.round((this.currDonationsTotal / this.donationGoal) * 100);
        return `${percent}%`;
      }
    },
    cssProps() {
      return {
        '--text-color': this.textColor,
        '--donation-bar-base-color': this.donationBarBaseColor,
        '--donation-bar-fill-color': this.donationBarFillColor,
        '--donation-bar-fill-width': this.donationBarFillWidth
      };
    },
    formattedEndDate() {
      if (!this.endDate) return null;

      const date = new Date(this.endDate);
      const now = new Date();

      const yearFromNow = new Date(new Date().setFullYear(now.getFullYear() + 1));

      let options = {month: 'short', day: 'numeric'};
      if (date > yearFromNow) {
        options = {month: 'short', year: 'numeric'};
      }
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

      if (date >= now) {
        return `Ends ${formattedDate}`;
      } else {
        return `Ended ${formattedDate}`;
      }
    }
  },
  watch: {},
  mounted() {
  },
  methods: {
    moneyFormat(val) {
      return ApplicationHelper.formatMoney(val, true, '$0');
    },
  }
});

</script>

<template>
  <div
    class="donation-info"
    :style="cssProps"
  >
    <!-- Show Amount Raised Only -->
    <div
      v-if="showAmount && !showBar && donationGoal"
      class="caption-regular mb-2"
    >
      <span class="font-bold text-start">
        {{ moneyFormat(currDonationsTotal) }} Raised
      </span>
    </div>

    <!-- Show Progress Bar + Raised + Goal -->
    <div
      v-if="showBar && donationGoal"
      class="donation-bar-container"
    >
      <div class="caption-regular flex justify-between mb-2 w-full">
        <div class="font-bold">
          {{ moneyFormat(currDonationsTotal) }} Raised
        </div>
        <div>
          {{ moneyFormat(donationGoal) }} Goal
        </div>
      </div>
      <donation-progress-bar
        :donation-target="donationGoal"
        :total-donations="currDonationsTotal"
        :fill-color="donationBarFillColor"
        :background-color="donationBarBaseColor"
        size="small"
      />
    </div>

    <!-- No Goal: Show only total raised -->
    <div
      v-if="!donationGoal"
      class="fund-donation-total"
    >
      <span :class="(largeTotal) ? 'subtitle-bold' : 'body-1-bold'">
        {{ moneyFormat(currDonationsTotal) }}
      </span>
      <span :class="(largeTotal) ? 'subtitle-regular' : 'body-1-regular'">
        Raised
      </span>
      <div
        v-if="formattedEndDate"
        class="text-end md:ml-auto font-bold caption-regular"
      >
        {{ formattedEndDate }}
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.donation-bar-container {
  .donation-bar {
    width: 100%;
    height: 16px;
    background-color: var(--donation-bar-base-color);
    border-radius: $radius-rounded;
    border: 1px solid var(--donation-bar-fill-color);

    .donation-bar-fill {
      height: 100%;
      width: var(--donation-bar-fill-width);
      border-radius: $radius-rounded;
      background-color: var(--donation-bar-fill-color);
    }
  }

  .caption-regular {
    margin-top: $space-xs;

    span {
      font-weight: bold;
    }
  }
}

.fund-donation-total {
  display: flex;
  gap: $space-xs;

  span:first-child {
    color: var(--text-color);
  }
}
</style>
